import { useEffect, useRef, useState } from "react";
import { Button } from "reactstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ApiService from "../../services/apiService";
import { toast } from "react-toastify";
import ProgressBar from "./ProgressBar";

type Props = {
  totalValves: number;
  ChakId: number;
  onBack: () => void;
};

const FULL_VALVE_COUNT = 14;

const days = [
  { name: "Mon",  bitIndex: 0 },  // bit 0
  { name: "Tue",  bitIndex: 1 },  // bit 1
  { name: "Wed",  bitIndex: 2 },  // bit 2
  { name: "Thur", bitIndex: 3 },  // bit 3
  { name: "Fri",  bitIndex: 4 },  // bit 4
  { name: "Sat",  bitIndex: 5 },  // bit 5
  { name: "Sun",  bitIndex: 6 },  // bit 6
];
const bitsToDecimal = (selected: number[], offset = 0): string => {
  let result = 0;
  selected.forEach((i) => { result |= (1 << (i + offset)); });
  return result.toString();
};
const ScheduelPopup = ({ totalValves, ChakId, onBack }: Props) => {
  const [scheduleNo,     setScheduleNo]     = useState<string>("");
  const [isEnable,       setIsEnable]       = useState<boolean>(false);
  const [startDate,      setStartDate]      = useState<Date | null>(null);
  const [endDate,        setEndDate]        = useState<Date | null>(null);
  const [startTime,      setStartTime]      = useState<string>("");
  const [duration,       setDuration]       = useState<string>("");
  const [volume,         setVolume]         = useState<string>("");
  const [selectedDays,   setSelectedDays]   = useState<number[]>([]);
  const [selectedValves, setSelectedValves] = useState<number[]>([]);

  const [cmdLoading, setCmdLoading] = useState(false);
  const [progNow,    setProgNow]    = useState(0);
  const progInterval = useRef<NodeJS.Timeout | null>(null);

  // ── Progress bar ──────────────────────────────────────────────
  const startProgress = () => {
    if (progInterval.current) clearInterval(progInterval.current);
    setProgNow(0);
    setCmdLoading(true);
    const increment = 100 / (25000 / 250);
    let current = 0;
    progInterval.current = setInterval(() => {
      current += increment;
      if (current >= 100) {
        current = 100;
        clearInterval(progInterval.current!);
        progInterval.current = null;
      }
      setProgNow(current);
    }, 250);
  };

  const stopProgress = () => {
    if (progInterval.current) { clearInterval(progInterval.current); progInterval.current = null; }
    setProgNow(0);
    setCmdLoading(false);
  };

  // ── Fetch existing schedule ───────────────────────────────────
  useEffect(() => {
    const fetchSchedule = async () => {
      try {
        const res = await ApiService.axiosApi(
          `/Schedule/GetScheduleByDeviceId/${ChakId}`,
          { method: "GET" }
        );
        if (res.success && res.data) {
          const d = res.data.data ?? res.data;
          setScheduleNo(String(d.scheduleNo ?? ""));
          setIsEnable(!!d.enable);
          if (d.startDate) setStartDate(new Date(Number(d.startDate) * 1000));
          if (d.endDate)   setEndDate(new Date(Number(d.endDate) * 1000));
          if (d.startTime) setStartTime(d.startTime);
          setDuration(String(d.duration ?? ""));
          setVolume(String(d.volume ?? ""));

          if (d.weekDays) {
            const num = Number(d.weekDays);
            const sel: number[] = [];
            for (let i = 0; i < 7; i++) {        // 👈 7 bits
              if (num & (1 << i)) sel.push(i);   // 👈 offset nahi
            }
            setSelectedDays(sel);
          }
          if (d.valveNumber) {
            const num = Number(d.valveNumber);
            const sel: number[] = [];
            for (let i = 2; i < FULL_VALVE_COUNT; i++) {  // 👈 2 se start — SOV1,2 skip
              if (num & (1 << i)) sel.push(i - 2);         // 👈 -2 offset — SOV3→0, SOV4→1
            }
            setSelectedValves(sel);
          }
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchSchedule();
  }, [ChakId]);

  const toggleDay = (bitIndex: number, checked: boolean) => {
    setSelectedDays((prev) =>
      checked ? [...prev, bitIndex] : prev.filter((i) => i !== bitIndex)
    );
  };

  const toggleValve = (index: number, checked: boolean) => {
    setSelectedValves((prev) =>
      checked ? [...prev, index] : prev.filter((i) => i !== index)
    );
  };

  const postSchedule = async () => {
    startProgress();
    try {
      const payload = {
        deviceId:    ChakId,
        scheduleNo:  parseInt(scheduleNo),
        enable:      isEnable,
        startDate:   startDate ? String(Math.floor(startDate.getTime() / 1000)) : "",
        endDate:     endDate   ? String(Math.floor(endDate.getTime()   / 1000)) : "",
        startTime,
        duration,
        volume,
        weekDays:    bitsToDecimal(selectedDays),
        valveNumber: bitsToDecimal(selectedValves, 2),
      };

      const res = await ApiService.axiosApi("Command/addschedule", {
        method: "POST",
        data: payload,
      });

      if (res.success) {
        toast.success("✅ Schedule saved");
        onBack();
      } else {
        toast.error("Failed to schedule");
      }
    } catch (e) {
      console.error(e);
    } finally {
      stopProgress();
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "6px 10px",
    fontSize: 13,
    borderRadius: 6,
    border: "1px solid #c5dede",
    background: "white",
    outline: "none",
  };

  const sectionLabel: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 700,
    color: "#2b5e5e",
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
  };

  return (
    <div style={{ padding: "16px 20px", position: "relative" }}>

      {/* ── Progress bar ── */}
      {cmdLoading && (
        <ProgressBar
          totalpage={0}
          pageNumber={0}
          now={progNow}
          mystyle={{
            position: "absolute",
            top: "-97px",
            bottom: 0,
            left: '-16px',
            height: "4px",
            width: "1138px",
            zIndex: 10,
            backgroundColor: "transparent",
            borderRadius: 0,
          }}
        />
      )}

      {/* ── Enable ── */}
      <div className="d-flex align-items-center mb-3">
        <span style={{ fontSize: 13, marginRight: 10, color: "#2b5e5e", fontWeight: 600 }}>Enable:</span>
        <label className="toggle-switch">
          <input
            type="checkbox"
            checked={isEnable}
            onChange={(e) => setIsEnable(e.target.checked)}
          />
          <span className="slider"></span>
        </label>
      </div>

      <hr style={{ borderColor: "#e0efef", margin: "10px 0" }} />

      {/* ── Dates + Time ── */}
      <div className="d-flex gap-3 mb-3">
        <div style={{ flex: 1 }}>
          <div style={sectionLabel}>Start Date</div>
          <DatePicker
            className="volume-scheduel always-bordered"
            dateFormat="dd/MM/yyyy"
            placeholderText="DD/MM/YYYY"
            selected={startDate}
            onChange={(d) => setStartDate(d)}
            isClearable
            disabled={cmdLoading}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={sectionLabel}>End Date</div>
          <DatePicker
            className="volume-scheduel always-bordered"
            dateFormat="dd/MM/yyyy"
            placeholderText="DD/MM/YYYY"
            selected={endDate}
            onChange={(d) => setEndDate(d)}
            isClearable
            disabled={cmdLoading}
          />
        </div>
        <div style={{ flex: 1 }}>
          <div style={sectionLabel}>Start Time</div>
          <input
            type="time"
            style={{ ...inputStyle, opacity: cmdLoading ? 0.6 : 1 }}
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            disabled={cmdLoading}
          />
        </div>
      </div>

      <hr style={{ borderColor: "#e0efef", margin: "10px 0" }} />

      {/* ── Week days ── */}
      <div style={sectionLabel}>Week Days</div>
      <div className="d-flex flex-wrap gap-2 mb-3">
        {days.map((day) => {
          const active = selectedDays.includes(day.bitIndex);
          return (
            <div
              key={day.bitIndex}
              onClick={() => !cmdLoading && toggleDay(day.bitIndex, !active)}
              style={{
                padding: "4px 14px",
                borderRadius: 20,
                border: `1.5px solid ${active ? "#2b5e5e" : "#c5dede"}`,
                background: active ? "#2b5e5e" : "white",
                color: active ? "white" : "#2b5e5e",
                fontSize: 12,
                fontWeight: active ? 700 : 400,
                cursor: cmdLoading ? "not-allowed" : "pointer",
                opacity: cmdLoading ? 0.6 : 1,
                userSelect: "none",
                transition: "all 0.2s",
              }}
            >
              {day.name}
            </div>
          );
        })}
      </div>

      <hr style={{ borderColor: "#e0efef", margin: "10px 0" }} />

      {/* ── Schedule No / Duration / Volume ── */}
      <div className="d-flex gap-3 mb-3">
        <div style={{ flex: 1 }}>
          <div style={sectionLabel}>Schedule No</div>
          <input type="number" style={{ ...inputStyle, opacity: cmdLoading ? 0.6 : 1 }} value={scheduleNo} onChange={(e) => setScheduleNo(e.target.value)} disabled={cmdLoading} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={sectionLabel}>Duration</div>
          <input type="number" style={{ ...inputStyle, opacity: cmdLoading ? 0.6 : 1 }} value={duration} onChange={(e) => setDuration(e.target.value)} disabled={cmdLoading} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={sectionLabel}>Volume</div>
          <input type="number" style={{ ...inputStyle, opacity: cmdLoading ? 0.6 : 1 }} value={volume} onChange={(e) => setVolume(e.target.value)} disabled={cmdLoading} />
        </div>
      </div>

      <hr style={{ borderColor: "#e0efef", margin: "10px 0" }} />

      {/* ── Valves ── */}
      <div style={sectionLabel}>Valves</div>
      <div className="d-flex flex-wrap gap-2 mb-4">
        {Array.from({ length: totalValves }, (_, i) => {
          const active = selectedValves.includes(i);
          return (
            <div
              key={i}
              onClick={() => !cmdLoading && toggleValve(i, !active)}
              style={{
                padding: "4px 14px",
                borderRadius: 20,
                border: `1.5px solid ${active ? "#89AC46" : "#c5dede"}`,
                background: active ? "#89AC46" : "white",
                color: active ? "white" : "#2b5e5e",
                fontSize: 12,
                fontWeight: active ? 700 : 400,
                cursor: cmdLoading ? "not-allowed" : "pointer",
                opacity: cmdLoading ? 0.6 : 1,
                userSelect: "none",
                transition: "all 0.2s",
              }}
            >
              V{i + 1}
            </div>
          );
        })}
      </div>

      {/* ── Footer ── */}
      <div className="d-flex justify-content-end gap-2">
        <Button color="danger"  size="sm" disabled={cmdLoading} onClick={onBack}>Cancel</Button>
        <Button color="primary" size="sm" disabled={cmdLoading} onClick={postSchedule}>
          {cmdLoading ? "Saving..." : "Save Schedule"}
        </Button>
      </div>

    </div>
  );
};

export default ScheduelPopup;
